export type EchoFilterCheckbox = {
    id: string | boolean | number;
    isSelected: boolean;
    isDisabled: boolean;
    isGroup: boolean;
    isHidden: boolean;
    name: string;
    children: EchoFilterCheckbox[];
    allChildrenSelected?: boolean;
    parentFullySelected?: boolean;
};

/** A level filter tree node. `id` is only a unique key for the tree; select on `groupId` and `agentId`. */
export type EchoLevelFilterCheckbox = Omit<EchoFilterCheckbox, 'id' | 'children'> & {
    id: string;
    groupId: string;
    /** Set on an agent leaf: the agent, not an agent card. */
    agentId?: string | null;
    children: EchoLevelFilterCheckbox[];
};

/** Whole groups, and agents selected in a group. A group is never in both. */
export type LevelFilterRequest = {
    groupIds: string[];
    agents: { groupId: string; agentId: string }[];
    /** Keeps a filter whose selections were all deleted matching nothing, not everything. */
    matchesNothing?: boolean;
};

export type EchoFilterKeyword = {
    excludedWords: string[];
    selectedWordSearchType: 'And' | 'Or';
    availableWordSearchTypes: ('And' | 'Or')[];
    words: string[];
};

export type CustomDataFilterType = 'Checkbox' | 'Text' | 'SearchableCheckbox';

export type CustomDataValuePair = {
    id: number;
    name: string;
};

export type EchoFilterCustomRequestData = {
    key: string;
    selectedValuePairs?: CustomDataValuePair[];
    words?: string[];
};

export type EchoFilterCustomResponseData = {
    filterType: CustomDataFilterType;
    key: string;
    name: string;
    filter: Array<EchoFilterCheckbox> | undefined;
    words: Array<string> | undefined;
};

/** Filter types whose selection is a set of checked values rather than free-text words. */
export const isCheckboxFilterType = (filterType: CustomDataFilterType): boolean =>
    filterType === 'Checkbox' || filterType === 'SearchableCheckbox';

export type CustomDataValueMatch = {
    id: number;
    value: string;
};

export type ResponseSearchCustomDataValues = {
    values: CustomDataValueMatch[];
};

export type EchoFilter = {
    startTime: string;
    endTime: string;
    relativeDateRange?: RelativeDateRangeDto | null;
    keywordFilter?: EchoFilterKeyword;
    trackerFilter?: {
        availableGroupTypes: string[];
        selectedGroupType?: string;
        filter: EchoFilterCheckbox[];
    };
    levelFilter?: EchoLevelFilterCheckbox[];
    /** The level filter had selections but none are left, so it matches nothing. */
    levelFilterMatchesNothing?: boolean;
    skillFilter?: EchoFilterCheckbox[];
    channelFilter?: EchoFilterCheckbox[];
    categoryFilter?: EchoFilterCheckbox[];
    sentimentAgentFilter?: EchoFilterCheckbox[];
    sentimentAgentDirectionFilter?: EchoFilterCheckbox[];
    sentimentCustomerFilter?: EchoFilterCheckbox[];
    sentimentCustomerDirectionFilter?: EchoFilterCheckbox[];
    queueFilter?: EchoFilterCheckbox[];
    customDataFilter?: Array<EchoFilterCustomResponseData>;
    recordingStart?: number;
    recordingEnd?: number;
    onlyResolved?: boolean | null;
    filterKeyToPropertyMap?: FilterKeyToPropertyMap;
    protocolFormDefinitionFilter?: EchoFilterCheckbox[];
    submittedByUserFilter?: EchoFilterCheckbox[];
    protocolAgentFilter?: EchoFilterCheckbox[];
    protocolGradeFilter?: EchoFilterCheckbox[];
    isNew?: boolean | undefined;
    submittedByMe?: boolean | null;
    createdByAi?: boolean | null;
    editedByHuman?: boolean | null;
    pendingApproval?: boolean | null;
    actionRequired?: boolean | null;
    hasTranscriptions: boolean;
} | null;

export type EchoFilterRequest = {
    startTime: string | null;
    endTime: string | null;
    relativeDateRange?: RelativeDateRangeDto | null;
    keywordFilter?: {
        type: string;
        words: string[];
        excludedWords: string[];
    };
    trackerFilter?: {
        type: string;
        selectedIds: string[];
    };
    levelFilter?: LevelFilterRequest | null;
    skillFilter?: string[];
    channelFilter?: string[];
    categoryFilter?: number[];
    sentimentAgentFilter?: number[];
    sentimentAgentDirectionFilter?: string[];
    sentimentCustomerFilter?: number[];
    sentimentCustomerDirectionFilter?: string[];
    queueFilter?: string[];
    customDataFilter?: EchoFilterCustomRequestData[];
    recordingStart?: number | null;
    recordingEnd?: number | null;
    onlyResolved?: boolean | null;
    protocolFormDefinitionFilter?: string[];
    submittedByUserFilter?: string[];
    protocolAgentFilter?: string[];
    protocolGradeFilter?: string[];
    isNew?: boolean | null;
    submittedByMe?: boolean | null;
    createdByAi?: boolean | null;
    editedByHuman?: boolean | null;
    pendingApproval?: boolean | null;
    actionRequired?: boolean | null;
    hasTranscriptions: boolean;
};

export type EchoSelectedFilter = {
    startTime: string;
    endTime: string;
    relativeDateRange?: RelativeDateRangeDto | null;
    keywordFilter?: EchoFilterKeyword;
    trackerFilter?: {
        selectedGroupType?: string;
        filter: EchoFilterCheckbox[];
    };
    levelFilter?: EchoFilterCheckbox[];
    skillFilter?: EchoFilterCheckbox[];
    channelFilter?: EchoFilterCheckbox[];
    categoryFilter?: EchoFilterCheckbox[];
    sentimentAgentFilter?: EchoFilterCheckbox[];
    sentimentAgentDirectionFilter?: EchoFilterCheckbox[];
    sentimentCustomerFilter?: EchoFilterCheckbox[];
    sentimentCustomerDirectionFilter?: EchoFilterCheckbox[];
    queueFilter?: EchoFilterCheckbox[];
    customDataFilter?: EchoFilterCustomResponseData[];
    recordingStart?: number | null;
    recordingEnd?: number | null;
    onlyResolved?: boolean | null;
    protocolFormDefinitionFilter?: EchoFilterCheckbox[];
    submittedByUserFilter?: EchoFilterCheckbox[];
    protocolAgentFilter?: EchoFilterCheckbox[];
    protocolGradeFilter?: EchoFilterCheckbox[];
    isNew?: boolean | undefined;
    submittedByMe?: boolean | null;
    createdByAi?: boolean | null;
    editedByHuman?: boolean | null;
    pendingApproval?: boolean | null;
    actionRequired?: boolean | null;
    hasTranscriptions: boolean;
};

export type FilterKeyToPropertyMap = {
    Keyword: 'keywordFilter';
    Tracker: 'trackerFilter';
    Level: 'levelFilter';
    Skill: 'skillFilter';
    Category: 'categoryFilter';
    Channel: 'channelFilter';
    SentimentAgent: 'sentimentAgentFilter';
    SentimentCustomer: 'sentimentCustomerFilter';
    SentimentAgentDirection: 'sentimentAgentDirectionFilter';
    SentimentCustomerDirection: 'sentimentCustomerDirectionFilter';
    Queue: 'queueFilter';
    OnlyResolved: 'onlyResolved';
    ProtocolFormDefinition: 'protocolFormDefinitionFilter';
    SubmittedByUser: 'submittedByUserFilter';
    ProtocolAgent: 'protocolAgentFilter';
    ProtocolGrade: 'protocolGradeFilter';
    IsNew: 'isNew';
    ProtocolCoachedByName: 'coachedByName';
    ProtocolAgentName: 'protocolAgentName';
    ProtocolSubmittedByMe: 'submittedByMe';
    ProtocolCreatedByAi: 'createdByAi';
    ProtocolPendingApproval: 'pendingApproval';
    ProtocolActionRequired: 'actionRequired';
};

export type ResponseGetFavoriteFilters = IFavoriteFilter[];

export enum FilterStoreName {
    SharedFilter = 'shared-filter',
    AskMeFilter = 'ask-me-filter',
    WidgetFilter = 'widget-filter',
    ScheduleCoachingFilter = 'schedule-coaching-filter',
}

export type IFavoriteFilter = {
    id: string;
    name: string;
};

export type FavoriteFilterList = Pick<IFavoriteFilter, 'id' | 'name'>[];

export type FavoriteFilterListResponse = {
    favoriteFilters: FavoriteFilterList;
};

export type RequestPostFavoriteFilter = {
    name: string;
    favoriteFilter: EchoFilterRequest;
};

export type ResponsePostFavoriteFilter = {
    id: string;
    name: string;
    favoriteFilter: EchoFilterRequest;
};

export type RequestDeleteFavoriteFilter = { id: string };

export type RequestGetFavoriteFilter = {
    id: string;
};

export type ResponseGetFavoriteFilter = {
    id: string;
    name: string;
    favoriteFilter: EchoFilterRequest;
};

export enum RelativeRangePreset {
    Today = 0,
    Yesterday = 1,
    ThisWeek = 2,
    LastWeek = 3,
    MonthToDate = 4,
    LastMonth = 5,
    YearToDate = 6,
    LastNDays = 7,
    LastNMonths = 8,
}

export type RelativeDateRangeDto = {
    preset: RelativeRangePreset;
    value?: number | null;
};
